import dynamic from 'next/dynamic';
import { useRef, useState } from 'react';
import type CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import { hasFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import useUser from '@alltrails/context/hooks/useUser';
import type { User } from '@alltrails/types/User';

const AskUpsellModal = dynamic(() => import('@/components/AskSidebar/AskUpsellModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

export const canAsk = (user: User | null) => !!user && hasFeatureEntitlement(user, 'ask_alltrails');

type Args = {
  signUpTrigger: CarouselDisplayTrigger;
};

/**
 * Lets the user ask Ask AllTrails something. Logged-out users get the sign-up modal and free users the upsell.
 */
const useAskAccess = ({ signUpTrigger }: Args) => {
  const user = useUser();
  const pendingAction = useRef<() => void>(undefined);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);

  const withAskAccess = (action: () => void) => {
    if (canAsk(user)) {
      action();
      return;
    }

    pendingAction.current = action;

    if (user) {
      setIsUpsellOpen(true);
    } else {
      setIsSignUpOpen(true);
    }
  };

  const runPendingAction = () => {
    const action = pendingAction.current;
    pendingAction.current = undefined;
    action?.();
  };

  const modals = (
    <>
      {isSignUpOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpOpen(false)}
          hideUpsell
          isOpen
          onSuccess={signedUpUser => {
            setIsSignUpOpen(false);

            if (canAsk(signedUpUser)) {
              runPendingAction();
            } else {
              setIsUpsellOpen(true);
            }
          }}
          trigger={signUpTrigger}
          // TODO(XPLR-256): real analytics values
          upsellTrigger="Banner on Explore"
        />
      )}
      {isUpsellOpen && (
        <AskUpsellModal
          isOpen
          onCloseRequest={() => setIsUpsellOpen(false)}
          onSuccess={() => {
            setIsUpsellOpen(false);
            runPendingAction();
          }}
        />
      )}
    </>
  );

  return { modals, withAskAccess };
};

export default useAskAccess;
